import { AlertCircle, MicOff, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FolioDictation, SpeechToTextErrorReason } from '../../components/speech-to-text';
import { Badge } from '@folio/components/ui/badge';

const ERROR_VISUAL: Record<
  SpeechToTextErrorReason,
  { Icon: typeof MicOff; tone: 'amber' | 'muted' }
> = {
  permission_denied: { Icon: MicOff, tone: 'amber' },
  network_error: { Icon: WifiOff, tone: 'muted' },
  unauthorized: { Icon: AlertCircle, tone: 'muted' },
  max_duration_reached: { Icon: AlertCircle, tone: 'muted' },
  unsupported_browser: { Icon: AlertCircle, tone: 'muted' },
};

const TONE = { amber: 'warning', muted: 'muted' } as const;

export function DictationLiveStatus({
  recording,
  dictation,
}: {
  recording: boolean;
  dictation: FolioDictation;
}) {
  const { t } = useTranslation();
  if (recording) {
    return (
      <Badge variant="light" tone="destructive" size="sm" className="gap-2" aria-live="polite">
        <span className="relative flex h-2 w-2" aria-hidden>
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
        </span>
        {t('speechToText.listening', 'Écoute en cours…')}
      </Badge>
    );
  }
  if (!dictation.error) return null;
  const visual = ERROR_VISUAL[dictation.error] ?? ERROR_VISUAL.network_error;
  const Icon = visual.Icon;
  return (
    <Badge
      variant="light"
      tone={TONE[visual.tone]}
      size="sm"
      className="gap-2"
      role="status"
      aria-live="polite"
    >
      <Icon className="size-3.5 shrink-0" aria-hidden />
      <span>{t(`speechToText.${dictation.error}`, dictation.error)}</span>
    </Badge>
  );
}
